import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  X,
  Store,
  ToggleLeft,
  ToggleRight,
} from 'lucide-angular';
import {
  StoreTypesService,
  CreateStoreTypeRequest,
  UpdateStoreTypeRequest,
} from '../../../core/services/store-types.service';
import { StoreTypeDto } from '../../../core/models/store.models';

@Component({
  selector: 'app-admin-store-types',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './store-types.component.html',
})
export class AdminStoreTypesComponent implements OnInit {
  private svc = inject(StoreTypesService);
  private readonly destroyRef = inject(DestroyRef);

  Plus = Plus;
  Edit = Edit;
  Trash2 = Trash2;
  RefreshCw = RefreshCw;
  X = X;
  Store = Store;
  ToggleLeft = ToggleLeft;
  ToggleRight = ToggleRight;

  storeTypes = signal<StoreTypeDto[]>([]);
  loading = signal(false);
  submitting = signal(false);

  dialogOpen = signal(false);
  editingId = signal<string | null>(null);

  form: { name: string; code: string; icon: string; isActive: boolean } = {
    name: '',
    code: '',
    icon: '',
    isActive: true,
  };

  get isEditMode() {
    return !!this.editingId();
  }

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc
      .getAll(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.storeTypes.set(res.data ?? []);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  openCreate() {
    this.editingId.set(null);
    this.form = { name: '', code: '', icon: '', isActive: true };
    this.dialogOpen.set(true);
  }

  openEdit(st: StoreTypeDto) {
    this.editingId.set(st.id);
    this.form = { name: st.name, code: st.code, icon: st.icon ?? '', isActive: st.isActive };
    this.dialogOpen.set(true);
  }

  closeDialog() {
    this.dialogOpen.set(false);
  }

  save() {
    if (!this.form.name.trim() || (!this.isEditMode && !this.form.code.trim())) return;
    this.submitting.set(true);

    if (this.isEditMode) {
      const req: UpdateStoreTypeRequest = {
        name: this.form.name.trim(),
        icon: this.form.icon.trim() || null,
        isActive: this.form.isActive,
      };
      this.svc
        .update(this.editingId()!, req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.submitting.set(false);
            this.closeDialog();
            this.load();
          },
          error: () => this.submitting.set(false),
        });
    } else {
      const req: CreateStoreTypeRequest = {
        name: this.form.name.trim(),
        code: this.form.code.trim().toLowerCase(),
        icon: this.form.icon.trim() || null,
      };
      this.svc
        .create(req)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.submitting.set(false);
            this.closeDialog();
            this.load();
          },
          error: () => this.submitting.set(false),
        });
    }
  }

  delete(id: string) {
    if (!confirm("Do'kon turini o'chirishni tasdiqlaysizmi?")) return;
    this.svc
      .delete(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.load() });
  }
}
